import styles from './PhaseTrack.module.css';

export type Phase = 'Base' | 'Build' | 'Peak' | 'Taper';

type PhaseTrackProps = {
  phase: Phase;
  /** 0–100. Progress bar fill + pin position. */
  progressPct: number;
  /** dark = sits on --ink-900 card (Championship rail on desktop/mobile).
   *  light = sits on --paper/cream (used in week plan headers, etc.). */
  variant?: 'dark' | 'light';
  /** No-race state: greys out everything, hides the fill + pin. */
  muted?: boolean;
  className?: string;
};

const phases: Phase[] = ['Base', 'Build', 'Peak', 'Taper'];

// Display labels. The third phase is internally 'Peak' but the rest of
// the app (SeasonGlance cells, mode pills) calls the competition phase
// "Comp"/"Competition"; render "Comp" here so the track stops being the
// terminology outlier — without churning the Phase type, its callers, or
// race.phase data. "Comp" (not "Competition") keeps the 25%-wide track
// cell from overflowing at mobile/rail widths.
const phaseLabel: Record<Phase, string> = {
  Base: 'Base',
  Build: 'Build',
  Peak: 'Comp',
  Taper: 'Taper',
};

/**
 * Base → Build → Peak → Taper horizontal progression bar with pin
 * position + labeled phase markers underneath.
 *
 * `dark` variant uses --today yellow for fill/pin/now-label (matches
 * the mockup's Championship Final card on --ink-900 bg).
 * `light` variant uses --signal marine (for in-body contexts on cream).
 * `muted` = no-race state: all greyed at rgba(255,255,255,.25), fill
 * and pin hidden.
 */
export function PhaseTrack({
  phase,
  progressPct,
  variant = 'dark',
  muted = false,
  className,
}: PhaseTrackProps) {
  const currentIndex = phases.indexOf(phase);
  const pct = Math.max(0, Math.min(100, progressPct));
  return (
    <div
      className={[styles.track, styles[variant], muted ? styles.muted : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.bar}>
        {!muted && (
          <>
            <div className={styles.fill} style={{ width: `${pct}%` }} />
            <div className={styles.pin} style={{ left: `${pct}%` }} />
          </>
        )}
      </div>
      <div className={styles.labels}>
        {phases.map((p, i) => (
          <span
            key={p}
            className={[
              i < currentIndex ? styles.done : '',
              i === currentIndex ? styles.now : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {phaseLabel[p]}
          </span>
        ))}
      </div>
    </div>
  );
}
